const escapeHTML=(value)=> String(value ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
const escapeCSV=(value)=> `"${String(value ?? '').replace(/"/g,'""')}"`;

const cleanBookmark=(bookmark)=> ({
  title: bookmark.title || '',url: bookmark.url || '',description: bookmark.description || '',tags: Array.isArray(bookmark.tags) ? bookmark.tags : [],folderId: bookmark.folderId || null,faviconurl: bookmark.faviconurl || null,sortOrder: Number(bookmark.sortOrder) || 0,dateAdded: bookmark.dateAdded ? new Date(bookmark.dateAdded).toISOString() : null,dateModified: bookmark.dateModified ? new Date(bookmark.dateModified).toISOString() : null
});

const cleanFolder=(folder)=> ({
  id: folder.id,name: folder.name || '',parent: folder.parent || null,sortOrder: Number(folder.sortOrder) || 0,dateAdded: folder.dateAdded ? new Date(folder.dateAdded).toISOString() : null,dateModified: folder.dateModified ? new Date(folder.dateModified).toISOString() : null
});

export const exportBookmarks=(bookmarks,folders,format='json')=> {
  if (format==='json') return exportToJSON(bookmarks,folders);
  if (format==='html') return exportToHTML(bookmarks,folders);
  if (format==='csv') return exportToCSV(bookmarks,folders);
  throw new Error('Unsupported export format');
};

const exportToJSON=(bookmarks,folders)=> JSON.stringify({
  product:'Bookmarks Manager Pro',version:'2.0',exportDate:new Date().toISOString(),folders:(folders || []).map(cleanFolder),bookmarks:(bookmarks || []).map(cleanBookmark)
},null,2);

const exportToHTML=(bookmarks,folders)=> {
  const allFolders=folders || [];
  const allBookmarks=bookmarks || [];
  const generateFolderHTML=(folderId=null,level=1)=> {
    const childFolders=allFolders.filter(folder=> (folder.parent || null)===folderId).sort((a,b)=> (a.sortOrder || 0)-(b.sortOrder || 0) || (a.name || '').localeCompare(b.name || ''));
    const folderBookmarks=allBookmarks.filter(bookmark=> (bookmark.folderId || null)===folderId).sort((a,b)=> (a.sortOrder || 0)-(b.sortOrder || 0));
    let html='';
    childFolders.forEach(folder=> {
      html+=`${'  '.repeat(level)}<DT><H3>${escapeHTML(folder.name)}</H3>\n`;
      html+=`${'  '.repeat(level)}<DL><p>\n${generateFolderHTML(folder.id,level+1)}${'  '.repeat(level)}</DL><p>\n`;
    });
    folderBookmarks.forEach(bookmark=> {
      html+=`${'  '.repeat(level)}<DT><A HREF="${escapeHTML(bookmark.url)}"`;
      if (bookmark.dateAdded) html+=` ADD_DATE="${Math.floor(new Date(bookmark.dateAdded).getTime()/1000)}"`;
      if (bookmark.faviconurl || bookmark.favicon) html+=` ICON="${escapeHTML(bookmark.faviconurl || bookmark.favicon)}"`;
      html+=`>${escapeHTML(bookmark.title || bookmark.url)}</A>\n`;
      if (bookmark.description) html+=`${'  '.repeat(level)}<DD>${escapeHTML(bookmark.description)}\n`;
    });
    return html;
  };

  return `<!DOCTYPE NETSCAPE-Bookmark-file-1>\n<!-- Bookmarks Manager Pro portable export -->\n<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">\n<TITLE>Bookmarks</TITLE>\n<H1>Bookmarks Menu</H1>\n<DL><p>\n${generateFolderHTML(null)}\n</DL><p>`;
};

const exportToCSV=(bookmarks,folders)=> {
  const folderMap=new Map((folders || []).map(folder=> [folder.id,folder]));
  const folderPath=(folderId)=> {
    const names=[];
    const seen=new Set();
    let current=folderMap.get(folderId);
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      names.unshift(current.name || 'Folder');
      current=current.parent ? folderMap.get(current.parent) : null;
    }
    return names.join(' / ');
  };
  const rows=[['Title','URL','Description','Tags','Folder','Date Added'].map(escapeCSV).join(',')];
  (bookmarks || []).forEach(bookmark=> rows.push([
    bookmark.title,bookmark.url,bookmark.description,(bookmark.tags || []).join(' | '),folderPath(bookmark.folderId),bookmark.dateAdded ? new Date(bookmark.dateAdded).toISOString() : ''
  ].map(escapeCSV).join(',')));
  return rows.join('\n');
};

export const importBookmarks=async (fileContent,format='json')=> {
  if (format==='json') return importFromJSON(fileContent);
  if (format==='html') return importFromHTML(fileContent);
  throw new Error('Restore supports Bookmarks Manager JSON and browser bookmark HTML files.');
};

const importFromJSON=(content)=> {
  let data;
  try {data=JSON.parse(content);} catch {throw new Error('Invalid JSON backup.');}
  if (!data || typeof data!=='object') throw new Error('Invalid JSON backup.');
  if (!Array.isArray(data.folders) || !Array.isArray(data.bookmarks)) throw new Error('This JSON file does not contain a bookmark library backup.');
  return {
    folders:data.folders.map(folder=> ({...folder,parent: folder.parent ?? folder.parentId ?? null})),
    bookmarks:data.bookmarks.map(bookmark=> ({...bookmark,tags:Array.isArray(bookmark.tags) ? bookmark.tags : []}))
  };
};

const importFromHTML=(content)=> {
  const parser=new DOMParser();
  const doc=parser.parseFromString(content,'text/html');
  const folders=[];
  const bookmarks=[];
  let folderCounter=1;
  let bookmarkCounter=1;

  const processDL=(dl,parentId=null)=> {
    if (!dl) return;
    const children=Array.from(dl.children);
    for (let index=0;index<children.length;index+=1) {
      const child=children[index];
      if (child.tagName!=='DT') continue;
      const h3=child.querySelector(':scope > H3') || child.querySelector('H3');
      const anchor=child.querySelector(':scope > A') || child.querySelector('A');
      if (h3) {
        const folderId=`imported-folder-${folderCounter++}`;
        folders.push({id:folderId,name:h3.textContent.trim() || 'Imported Folder',parent:parentId,sortOrder:folders.filter(folder=> folder.parent===parentId).length});
        let nested=child.querySelector(':scope > DL');
        if (!nested) {
          const next=children[index+1];
          if (next?.tagName==='DL') {nested=next;index+=1;}
        }
        processDL(nested,folderId);
      } else if (anchor) {
        const url=anchor.getAttribute('HREF') || anchor.getAttribute('href') || '';
        if (!/^https?:\/\//i.test(url)) continue;
        const next=children[index+1];
        const description=next?.tagName==='DD' ? next.textContent.trim() : '';
        if (next?.tagName==='DD') index+=1;
        const addDate=anchor.getAttribute('ADD_DATE');
        bookmarks.push({
          id:`imported-bookmark-${bookmarkCounter++}`,title:anchor.textContent.trim() || url,url,description,tags:[],folderId:parentId,faviconurl:anchor.getAttribute('ICON') || null,dateAdded:addDate ? new Date(Number.parseInt(addDate,10)*1000) : new Date(),sortOrder:bookmarks.filter(bookmark=> bookmark.folderId===parentId).length
        });
      }
    }
  };

  processDL(doc.querySelector('DL'));
  if (!folders.length && !bookmarks.length) throw new Error('No browser bookmarks were found in this HTML file.');
  return {folders,bookmarks};
};

export const downloadFile=(content,filename,type='application/json')=> {
  const blob=new Blob([content],{type});
  const url=URL.createObjectURL(blob);
  const anchor=document.createElement('a');
  anchor.href=url;
  anchor.download=filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(()=> URL.revokeObjectURL(url),0);
};